import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

export default function PendingApprovals() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const employeeId = localStorage.getItem("employeeId");
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, request: null, remark: "" });

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    // if (!roles.includes("ROLE_MANAGER") && !roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
  }, [navigate, token]);

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const roles = JSON.parse(localStorage.getItem("roles") || "[]");
      const isAdmin = roles.includes("ROLE_ADMIN");
      const isUser = roles.includes("ROLE_USER");
      const isManager = roles.includes("ROLE_MANAGER");

      let url = "";

      if (isAdmin || isUser) {
        // User also gets admin API access
        url = `${API_BASE_URL}/api/asset-requests?page=0&size=100`;
      } else if (isManager) {
        url = `${API_BASE_URL}/api/asset-requests/manager/${employeeId}`;
      }
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.ok ? await res.json() : {};
      const all = Array.isArray(data) ? data : data.content || [];
      setApprovals(all.filter(r => r.status === "Pending" || r.status === "PENDING"));
    } catch {
      setApprovals([]);
      toast.error("Unable to load pending approvals.");
    } finally { setLoading(false); }
  }, [token, employeeId]);

  useEffect(() => { fetchApprovals(); }, [fetchApprovals]);

  const handleAction = async (request, action, adminRemarks = "") => {
    try {
      setProcessingId(request.id);
      const body = { status: action === "approve" ? "Approved" : "Rejected" };
      if (adminRemarks.trim()) body.adminRemarks = adminRemarks.trim();
      const res = await fetch(`${API_BASE_URL}/api/asset-requests/${request.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success(action === "approve" ? "Request approved." : "Request rejected.");
      fetchApprovals();
    } catch (err) { toast.error(err.message || "Unable to process."); }
    finally { setProcessingId(null); }
  };

  const openRejectModal = (request) => setRejectModal({ open: true, request, remark: "" });
  const closeRejectModal = () => setRejectModal({ open: false, request: null, remark: "" });
  const confirmReject = () => {
    handleAction(rejectModal.request, "reject", rejectModal.remark);
    closeRejectModal();
  };

  const filtered = approvals.filter(r => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (r.assetCategory || "").toLowerCase().includes(q) ||
      (r.employeeName || "").toLowerCase().includes(q) ||
      (r.priority || "").toLowerCase().includes(q);
  });

  const PRIORITY_COLOR = {
    Critical: "bg-danger", High: "bg-warning text-dark",
    Medium: "bg-info text-dark", Low: "bg-secondary",
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />

      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="fw-bold mb-0"><i className="bi bi-hourglass-split me-2"></i> Pending Requests</h4>
            <div className="text-muted mt-1" style={{ fontSize: 13 }}>Review and act on employee asset allocation requests.</div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/approvalhistory")}>
              <i className="bi bi-clock-history me-1"></i> Approval History
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Back</button>
          </div>
        </div>

        <div className="row g-2 mb-3">
          {[
            { label: "Total Pending", value: approvals.length, icon: "bi-hourglass-split", color: "#fd7e14" },
            { label: "Critical", value: approvals.filter(r => r.priority === "Critical").length, icon: "bi-exclamation-circle", color: "#dc3545" },
            { label: "High Priority", value: approvals.filter(r => r.priority === "High").length, icon: "bi-arrow-up-circle", color: "#ffc107" },
          ].map(item => (
            <div className="col-md-4" key={item.label}>
              <div className="bg-white border rounded p-3 d-flex align-items-center gap-3">
                <i className={`bi ${item.icon}`} style={{ fontSize: 24, color: item.color }}></i>
                <div>
                  <div className="text-muted" style={{ fontSize: 12 }}>{item.label}</div>
                  <div className="fw-bold" style={{ fontSize: 22, color: item.color }}>{item.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-2 bg-light border mb-2" style={{ cursor: "pointer" }} onClick={() => setShowFilters(p => !p)}>
          <strong>{showFilters ? "− Filters" : "+ Filters"}</strong>
        </div>
        {showFilters && (
          <div className="row mb-3 g-2">
            <div className="col-md-4">
              <input type="text" className="form-control form-control-sm"
                placeholder="Search by asset, employee, priority..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="col-md-2">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setSearch("")}>Clear</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            <div className="text-muted mt-2" style={{ fontSize: 13 }}>Loading pending approvals...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-check2-all" style={{ fontSize: 32, opacity: 0.4 }}></i>
            <div className="mt-2">{search ? "No requests match your search." : "No pending approvals right now. All caught up!"}</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table table-sm align-middle text-start">
              <thead className="table-light">
                <tr>
                  <th>Sr.</th><th>Asset</th><th>Employee</th><th>Priority</th>
                  <th>Expected By</th><th>Justification</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, i) => (
                  <tr key={item.id}>
                    <td style={{ color: "#9ca3af", fontSize: 12 }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{item.assetCategory || "—"}</td>
                    <td>{item.employeeName || item.employeeId || "—"}</td>
                    <td>
                      {item.priority && (
                        <span className={`badge ${PRIORITY_COLOR[item.priority] || "bg-secondary"}`}>{item.priority}</span>
                      )}
                    </td>
                    <td>{item.expectedDate || "—"}</td>
                    <td style={{ maxWidth: 220 }}><small className="text-muted">{item.justification || "No justification"}</small></td>
                    <td>
                      <div className="d-flex gap-1">
                        <button className="btn btn-sm btn-outline-success"
                          onClick={() => handleAction(item, "approve")}
                          disabled={processingId === item.id}>Approve</button>
                        <button className="btn btn-sm btn-outline-danger"
                          onClick={() => openRejectModal(item)}
                          disabled={processingId === item.id}>Reject</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Reject Remark Modal ── */}
      {rejectModal.open && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={closeRejectModal}>
          <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", width: "100%", maxWidth: 460, padding: "28px 28px 22px" }}
            onClick={e => e.stopPropagation()}>
            <div className="d-flex align-items-center gap-3 mb-3">
              <div style={{ width: 42, height: 42, borderRadius: 10, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="bi bi-x-circle-fill" style={{ fontSize: 20, color: "#dc3545" }} />
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: 16 }}>Reject Request</div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  {rejectModal.request?.assetCategory} — {rejectModal.request?.employeeName}
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                Rejection Remark <span className="text-muted fw-normal">(optional)</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Provide a reason for rejection…"
                value={rejectModal.remark}
                onChange={e => setRejectModal(p => ({ ...p, remark: e.target.value }))}
                autoFocus
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-outline-secondary btn-sm px-4" onClick={closeRejectModal}>Cancel</button>
              <button className="btn btn-danger btn-sm px-4" onClick={confirmReject}
                disabled={processingId === rejectModal.request?.id}>
                {processingId === rejectModal.request?.id
                  ? <><span className="spinner-border spinner-border-sm me-1" />Rejecting…</>
                  : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
